import useSWR from "swr";
import {NotificationsRepository} from "@src/repositories/NotificationsRepository";
import {useParams} from "react-router";
import {ORDERS_REFRESH_INTERVAL} from "@src/config";
import {BaseRouteParams} from "@routes/api_routes";
import {useCompanyAddresses} from "@entities/company/lib/hooks";
import {OrdersListDTO} from "@dto/OrdersListDTO";

export const useCompanyNotifications = (load= true) => {
    const {accountId, companyId, companyAddressId,} = useParams<BaseRouteParams>()
    return useSWR(companyId && load && `/no-cache/${companyId}/notifications`, () => NotificationsRepository.getAll({
        accountId: accountId as string,
        companyId: companyId as string,
        companyAddressId: companyAddressId
    }), {
        refreshInterval: ORDERS_REFRESH_INTERVAL,
        dedupingInterval: ORDERS_REFRESH_INTERVAL
    })
}

export const useHasNewNotifications = (load = true) => {
    const {accountId, companyId, companyAddressId,} = useParams<BaseRouteParams>()
    const {isAllowed} = useCompanyAddresses()

    return useSWR(isAllowed && load && `/no-cache/notifications/has-new`, () => NotificationsRepository.hasNew({
        accountId: accountId as string,
        companyId: companyId as string,
        companyAddressId: companyAddressId
    }), {
        refreshInterval: ORDERS_REFRESH_INTERVAL,
        dedupingInterval: ORDERS_REFRESH_INTERVAL
    })
}

const isHasNotificationsInOrdersList = (ordersList?: OrdersListDTO): boolean => {
    return typeof ordersList?.hasNotifications === 'boolean'
}

/**
 * The orders list (already polled every minute) carries `hasNotifications`.
 * The separate has-new endpoint is polled only when the orders list has loaded
 * without that field (an API older than the field).
 */
export const useHasNotifications = (ordersList?: OrdersListDTO): boolean => {
    const isOrdersListLoaded = ordersList !== undefined
    const isAnsweredByOrdersList = isHasNotificationsInOrdersList(ordersList)
    const shouldLoadHasNew = isOrdersListLoaded && !isAnsweredByOrdersList
    const {data: hasNew} = useHasNewNotifications(shouldLoadHasNew)

    if (isAnsweredByOrdersList) {
        return ordersList?.hasNotifications === true
    }

    return hasNew?.hasNotifications ?? false
}
